/**
 * ANATOMY Quiz Results
 * Reads assessment from sessionStorage, renders the Molecular Damage Profile.
 * Each block type is rendered into its own container (placed by Liquid in block order).
 *
 * Adaptive mode (behind the quiz's `stream_assessment` setting): when the quiz
 * hands off a preliminary via sessionStorage['anatomy:quiz-prelim'], the
 * preliminary renders instantly and the full assessment is streamed beneath it
 * (SSE over fetch + ReadableStream). On connection loss it polls the same
 * single-flight assessment; incomplete upstream output is never shown as a result.
 */

/* Streamed prose field → results block container + final-styled element.
   Streaming writes into the SAME markup the final renderer uses, so there is no
   restyle flash when _renderBlocks() takes over on `complete`. */
const STREAM_FIELD_MAP = {
  damage_profile_headline: { type: 'heading', tag: 'h2', cls: 'qr__headline' },
  damage_profile_summary: { type: 'summary', tag: 'p', cls: 'qr__summary' },
  what_is_happening: { type: 'what_is_happening', tag: 'p', cls: 'qr__body' },
  key_insight: { type: 'key_insight', tag: 'div', cls: 'qr__insight' },
  why_previous_products_underperformed: { type: 'why_previous', tag: 'p', cls: 'qr__body' },
  how_anatomy_works_for_you: { type: 'how_anatomy', tag: 'p', cls: 'qr__body' },
  purchase_nudge: { type: 'purchase_nudge', tag: 'div', cls: 'qr__nudge' }
};

/* Self-contained styling for the streaming chrome (the results stylesheet is
   owned elsewhere; this keeps the stream UI within the quiz frontend scope). */
const QR_STREAM_CSS =
  '.qr-stream{max-width:760px;margin:0 auto 28px;}' +
  '.qr-prelim__card{border:1px solid rgb(var(--color-border));background:rgb(var(--color-foreground) / 0.03);padding:24px;display:grid;gap:14px;}' +
  '.qr-prelim__severity{justify-self:start;display:inline-flex;align-items:center;min-height:28px;padding:0 12px;border-radius:999px;font-size:11px;letter-spacing:.8px;text-transform:uppercase;background:rgb(var(--color-base-highlight) / 0.12);color:rgb(var(--color-base-highlight));}' +
  '.qr-prelim__headline{font-size:32px;line-height:1.08;letter-spacing:0;}' +
  '.qr-prelim__summary{font-size:15px;line-height:1.65;color:rgb(var(--color-foreground) / 0.75);}' +
  '.qr-prelim__insight{font-size:14px;line-height:1.6;color:rgb(var(--color-foreground) / 0.7);border-left:2px solid rgb(var(--color-base-highlight) / 0.4);padding-left:14px;}' +
  '.qr-progress{margin-top:18px;}' +
  '.qr-progress__track{height:4px;background:rgb(var(--color-foreground) / 0.08);border-radius:999px;overflow:hidden;}' +
  '.qr-progress__bar{height:100%;width:0%;background:rgb(var(--color-base-highlight));transition:width .4s ease;}' +
  '.qr-progress__label{margin-top:8px;font-size:12px;letter-spacing:.4px;color:rgb(var(--color-foreground) / 0.55);}' +
  '@media(max-width:767px){.qr-prelim__headline{font-size:26px;}}' +
  '@media (prefers-reduced-motion: reduce){.qr-progress__bar{transition:none;}}';

if (!customElements.get('anatomy-quiz-results')) {
  customElements.define('anatomy-quiz-results', class AnatomyQuizResults extends HTMLElement {

    connectedCallback() {
      this.config = JSON.parse(
        document.getElementById(`QuizResultsConfig-${this.dataset.sectionId}`).textContent
      );
      try { this.qaMode = new URLSearchParams(window.location.search).get('anatomy_qa') === '1'; }
      catch (_error) { this.qaMode = false; }
      if (this.qaMode) this.dataset.qaMode = 'true';

      this.loadingEl = this.querySelector('[data-results-loading]');
      this.emptyEl = this.querySelector('[data-results-empty]');
      this.containerEl = this.querySelector('[data-results-container]');
      this.debugEl = this.querySelector('[data-results-debug]');
      const contextRead = window.AnatomyQuizSupport && window.AnatomyQuizSupport.readResultContext
        ? window.AnatomyQuizSupport.readResultContext()
        : { context: window.AnatomyQuizSupport?.getResultContext?.() || null, status: 'unknown' };
      this.resultContext = contextRead.context;
      this.resultContextStatus = contextRead.status;
      this.resultContextExpired = contextRead.status === 'expired';
      this.currentResult = null;
      this.currentOffer = null;
      this.currentEmail = '';
      this.identitySaved = false;
      this.savedLinkView = false;
      this._offerPromise = null;
      this._identityCompletionPromise = null;
      this._recommendationCaptured = false;
      if (this.resultContext) this._applyResultContext(this.resultContext);
      this.identitySaved = this._hasAcceptedIdentity(this.resultContext);
      this.currentEmail = '';

      // Streaming state (only used when handed a preliminary by the adaptive quiz)
      this._streamEls = {};
      this._gotFirstToken = false;
      this._finalized = false;
      this._prelim = null;
      this._firstTokenTimer = null;
      this._hardCapTimer = null;
      this._lastAnnouncedPct = -1;

      this._loadResults();

      // Retake button
      this.addEventListener('click', (e) => {
        const retake = e.target.closest('[data-retake]');
        if (retake) {
          e.preventDefault();
          try {
            sessionStorage.removeItem('anatomy:quiz-results');
            sessionStorage.removeItem('anatomy:quiz-answers');
            sessionStorage.removeItem('anatomy:quiz-prelim');
            sessionStorage.removeItem('anatomy:quiz-stream-started');
            sessionStorage.removeItem('anatomy:quiz-aview');   // re-arm assessment-viewed once-guard for the retake
            sessionStorage.removeItem('anatomy:quiz-progress-v1');
            sessionStorage.removeItem('anatomy:saved-result-token');
            sessionStorage.removeItem('anatomy:quiz-result-context');
            sessionStorage.removeItem('anatomy:quiz-completion-v2');
            sessionStorage.removeItem('anatomy:quiz-offer');
            sessionStorage.removeItem('anatomy:quiz-identity-complete');
          } catch (err) { /* silent */ }
          const quizUrl = this.config.quizPageUrl || '/';
          window.location.href = quizUrl;
          return;
        }

        const addButton = e.target.closest('[data-consultation-add]');
        if (addButton) {
          e.preventDefault();
          this._addConsultationSystem(addButton);
          return;
        }

        const saveRetry = e.target.closest('[data-consultation-save-retry]');
        if (saveRetry) {
          e.preventDefault();
          this._finalizeKnownIdentity(this.pendingEmail || '');
        }
      });

      this.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-consultation-save-form]');
        if (!form) return;
        e.preventDefault();
        this._saveConsultation(form);
      });
    }

    disconnectedCallback() {
      this._finalized = true;   // stop any in-flight stream/fallback from mutating a detached node
      this._streamAbort?.abort();
      this._recoveryAbort?.abort();
      if (this._firstTokenTimer) { clearTimeout(this._firstTokenTimer); this._firstTokenTimer = null; }
      if (this._hardCapTimer) { clearTimeout(this._hardCapTimer); this._hardCapTimer = null; }
    }

    _applyResultContext(context) {
      this.sessionId = context.sessionId || '';
      this.sessionToken = context.sessionToken || '';
      this.sessionExpiresAt = Number(context.sessionExpiresAt) || 0;
      this.anatomyVisitorId = context.anatomyVisitorId || '';
      this.conversationHistory = Number(context.version) >= 2
        ? []
        : (Array.isArray(context.conversationHistory) ? context.conversationHistory : []);
      this.useAdaptiveBank = context.quizMode === 'adaptive_bank';
      this._legacyChipMode = context.quizMode === 'legacy_chip';
      this.questionCount = Number(context.totalQuestions)
        || Number(context.summary && context.summary.totalQuestions)
        || this.conversationHistory.length
        || 0;
      this._sessionPromise = null;
    }

    _hasAcceptedIdentity(context = this.resultContext) {
      if (context && context.identitySaved === true) return true;
      try {
        return !!this.sessionId
          && sessionStorage.getItem('anatomy:quiz-identity-complete') === this.sessionId;
      } catch (_error) {
        return false;
      }
    }

    visitorId() {
      const support = window.AnatomyQuizSupport;
      return support && support.analyticsAllowed && support.analyticsAllowed()
        ? (this.anatomyVisitorId || '')
        : '';
    }

    _loadResults() {
      // In the Shopify theme editor, show dummy data so blocks can be configured visually
      if (Shopify.designMode) {
        const dummy = this._getDummyResult();
        this._renderBlocks(dummy);
        this._afterResultsRendered(dummy, { designMode: true });
        this._renderDebug(dummy);
        return;
      }

      const savedResultToken = this._savedResultToken();
      if (savedResultToken) {
        this._loadSavedResult(savedResultToken);
        return;
      }

      try {
        const stored = sessionStorage.getItem('anatomy:quiz-results');
        if (stored) {
          const result = JSON.parse(stored);

          // The assessment response may nest the profile under a key
          // Try to find the actual profile data
          const profile = result.assessment || result.profile || result;
          if (this._isCompleteProfile(profile)) {
            this._renderBlocks(profile);
            this._afterResultsRendered(profile);
            this._renderDebug(result);
            this._finalized = true;
            this.dataset.resultsFinalized = 'true';
            this.dataset.resultsSource = 'cache';
            this._track('QuizResultsViewed', {
              damage_severity: profile.damage_severity || null,
              total_questions: result._meta?.total_questions || null
            });
            return;
          }
          sessionStorage.removeItem('anatomy:quiz-results');
        }
      } catch (e) {
        console.error('[Quiz Results] Error loading results:', e);
      }

      // Adaptive handoff: a preliminary was stored by the quiz. Render it
      // instantly (zero network), then stream (or non-stream-fetch) the full
      // assessment beneath it.
      try {
        const rawPrelim = sessionStorage.getItem('anatomy:quiz-prelim');
        if (rawPrelim) {
          const prelim = JSON.parse(rawPrelim);
          this._prelim = prelim;
          this._startAdaptiveResults(prelim);
          return;
        }
      } catch (e) {
        console.error('[Quiz Results] Error loading preliminary:', e);
      }

      this._showEmpty();
    }

    _savedResultToken() {
      const tokenPattern = /^[A-Za-z0-9_-]{43}$/;
      try {
        const fragment = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('result') || '';
        if (fragment) {
          if (!tokenPattern.test(fragment)) {
            sessionStorage.removeItem('anatomy:saved-result-token');
            return '';
          }
          sessionStorage.setItem('anatomy:saved-result-token', fragment);
          history.replaceState(null, '', window.location.pathname + window.location.search);
          return fragment;
        }
        const stored = sessionStorage.getItem('anatomy:saved-result-token') || '';
        return tokenPattern.test(stored) ? stored : '';
      } catch (_error) {
        return '';
      }
    }

    async _loadSavedResult(token) {
      const workerUrl = String(this.config.workerUrl || '').replace(/\/+$/, '');
      if (!workerUrl) {
        this._showEmpty('Saved consultation unavailable', 'We could not open this private result link. Start a new consultation to build another profile.');
        return;
      }

      try {
        const response = await fetch(`${workerUrl}/api/saved-result/${token}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          credentials: 'omit',
          cache: 'no-store'
        });
        if (response.status === 404 || response.status === 410) {
          sessionStorage.removeItem('anatomy:saved-result-token');
          sessionStorage.removeItem('anatomy:quiz-results');
          this._showEmpty('Saved consultation unavailable', 'This private result link has expired or is no longer available. Start a new consultation to build another profile.');
          return;
        }
        if (!response.ok) throw new Error(`saved result ${response.status}`);
        const data = await response.json();
        const profile = data && data.profile;
        if (!this._isCompleteProfile(profile)) throw new Error('saved result incomplete');

        sessionStorage.setItem('anatomy:quiz-results', JSON.stringify(profile));
        // An email link opens in a new tab with no live consultation session; the saved
        // record and its offer are the only authority on this page.
        this.savedLinkView = true;
        this._renderBlocks(profile);
        this._afterResultsRendered(profile, { offer: data.offer || null, identitySaved: true });
        this._renderDebug(profile);
        this._finalized = true;
        this.dataset.resultsFinalized = 'true';
        this.dataset.resultsSource = 'saved-link';
        this._track('QuizResultsViewed', {
          damage_severity: profile.damage_severity || null,
          render_path: 'saved_link'
        });
      } catch (error) {
        console.error('[Quiz Results] Saved result load failed:', error);
        this._showEmpty('Results temporarily unavailable', 'Your private result link is valid, but the profile could not be loaded right now. Please try the link again.');
      }
    }

    _getDummyResult() {
      return {
        damage_severity: 'moderate',
        in_scope: true,
        scope_note: null,
        damage_profile_headline: 'Your answers point to cumulative structural wear from chemical and thermal exposure',
        damage_profile_summary: 'The combination of chemical and thermal exposure maps to reduced elasticity and greater breakage through the processed lengths. The priority is rebuilding internal resilience, not surface feel alone.',
        what_is_happening: 'Chemical processing and repeated heat can weaken the internal structure that gives the fibre strength and flexibility. When both are present, the strand may become less tolerant of tension and more likely to snap through the most processed areas.',
        key_insight: 'The overlap between chemical and thermal exposure is the most useful place to focus the protocol.',
        why_previous_products_underperformed: 'Some conditioning approaches can improve surface feel without addressing the same internal structural mechanism, which may explain why the result felt temporary.',
        how_anatomy_works_for_you: 'ANATOMY uses click chemistry to create new covalent bonds at sites of damage. The Shampoo prepares and initiates reconstruction, the Conditioner delivers the core reconstruction step, and the Leave-In provides the highest-concentration, longest-contact support.',
        reconstruction_protocol: {
          entry_point: 'full_system',
          intensive_phase: 'Use the three steps together according to each product\u2019s directions for one complete usage cycle.',
          maintenance_phase: 'Continue with the protocol that fits your wash routine and the product directions.',
          expected_timeline: 'Assess the result after one complete usage cycle.',
          personalized_note: 'Keep the protocol consistent before changing more than one variable.'
        },
        clinical_proof: [
          { stat: 'SGS', context: 'formulas independently tested at SGS proderm, Germany, on bleached hair' },
          { stat: '-15%', context: 'E-Modulus change measured in the same single-fibre test' }
        ],
        purchase_nudge: 'If you want to address the structural pattern described here, begin with one complete reconstruction cycle and assess the result before changing the protocol.'
      };
    }

    _showEmpty(heading, text) {
      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.emptyEl) {
        const headingEl = this.emptyEl.querySelector('[data-results-empty-heading]');
        const textEl = this.emptyEl.querySelector('[data-results-empty-text]');
        if (headingEl && heading) headingEl.textContent = heading;
        if (textEl && text) textEl.textContent = text;
        this.emptyEl.hidden = false;
      }
    }

    _renderBlocks(result) {
      if (!this.containerEl) return;

      const blocks = this.config.blocks || [];
      const hideWithoutCommerce = new Set([
        'recommended_products', 'cta', 'custom_liquid', 'product_system',
        'bundle_cta', 'recommended_start', 'purchase_nudge', 'visual_proof'
      ]);

      blocks.forEach(block => {
        const el = this.containerEl.querySelector(`[data-block-id="${block.id}"]`);
        if (!el) return;

        if ((result.in_scope === false || result.commerce_allowed === false) && hideWithoutCommerce.has(block.type)) {
          el.hidden = true;
          return;
        }

        const html = this._renderBlock(block, result);
        if (html === null) {
          // No data for this block — hide it
          el.hidden = true;
        } else if (html !== undefined) {
          el.innerHTML = html;
        }
        // undefined = custom_liquid, already rendered server-side
      });

      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.emptyEl) this.emptyEl.hidden = true;
      this.containerEl.hidden = false;

      // Debug: log which blocks rendered vs had no data
      const rendered = blocks.filter(b => {
        const el = this.containerEl.querySelector(`[data-block-id="${b.id}"]`);
        return el && !el.hidden;
      });
      console.log(`[Quiz Results] Rendered ${rendered.length}/${blocks.length} blocks. Keys in result:`, Object.keys(result));
    }

    _renderBlock(block, result) {
      switch (block.type) {
        case 'severity_badge':
          return this._renderSeverity(result);
        case 'heading':
          return this._renderHeading(result, block.settings);
        case 'summary':
          return this._renderSummary(result);
        case 'what_is_happening':
          return this._renderLabelBody(result.what_is_happening, block.settings.label);
        case 'key_insight':
          return this._renderInsight(result);
        case 'why_previous':
          return this._renderLabelBody(result.why_previous_products_underperformed, block.settings.label);
        case 'how_anatomy':
          return this._renderLabelBody(
            result.how_anatomy_works_for_you,
            result.commerce_allowed === false ? 'Where molecular reconstruction applies' : block.settings.label
          );
        case 'protocol':
          return this._renderProtocol(result);
        case 'purchase_nudge':
          return this._renderNudge(result);
        case 'recommended_products':
          return this._renderProducts(result, block.settings);
        case 'cta':
          return this._renderCta(block.settings, result);
        case 'custom_liquid':
        case 'product_system':
        case 'bundle_cta':
        case 'recommended_start':
        case 'visual_proof':
        case 'save_results':
          return undefined; // rendered server-side
        default:
          return null;
      }
    }

    _renderSeverity(result) {
      const severity = result.damage_severity;
      if (!severity) return null;
      const noClearDamage = result.diagnostic_scope === 'no_clear_damage';
      const label = noClearDamage ? 'NO CLEAR DAMAGE' : this._formatSeverity(severity).toUpperCase();
      const modifier = noClearDamage ? 'no-clear-damage' : severity;
      return `<span class="qr__severity qr__severity--${this._esc(modifier)}">${this._esc(label)}</span>`;
    }

    // One severity formatter — shared by the final badge, the live stream
    // override, and the preliminary card so the label is always identical.
    // Hyphen-joined so moderate_severe reads "Moderate-Severe", not the
    // self-contradicting "Moderate Severe".
    _formatSeverity(value) {
      if (!value) return '';
      return String(value).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-');
    }

    _renderHeading(result, settings) {
      const text = result.damage_profile_headline || settings.fallback || 'Your Molecular Damage Profile';
      return `<h2 class="qr__headline">${this._esc(this._sentenceCase(text))}</h2>`;
    }

    _renderSummary(result) {
      if (!result.damage_profile_summary) return null;
      let html = `<p class="qr__summary">${this._esc(result.damage_profile_summary)}</p>`;
      if (result.scope_note) html += `<p class="qr__scope-note">${this._esc(result.scope_note)}</p>`;
      return html;
    }

    _renderLabelBody(content, label) {
      if (!content) return null;
      let html = '';
      if (label) html += `<p class="qr__label">${this._esc(label)}</p>`;
      html += `<p class="qr__body">${this._esc(content)}</p>`;
      return html;
    }

    _renderInsight(result) {
      if (!result.key_insight) return null;
      return `<div class="qr__insight">${this._esc(result.key_insight)}</div>`;
    }

    _renderProtocol(result) {
      if (!result.reconstruction_protocol) return null;
      const proto = result.reconstruction_protocol;
      const noProtocol = proto.entry_point === 'none';
      let html = `<div class="qr__protocol">`;
      const entryLabel = noProtocol ? 'No reconstruction protocol indicated' : (proto.entry_point?.replace(/_/g, ' ') || 'Full System');
      html += `<div class="qr__protocol-entry">${this._esc(entryLabel)}</div>`;
      if (!noProtocol && proto.intensive_phase) html += `<p><strong>Intensive phase:</strong> ${this._esc(proto.intensive_phase)}</p>`;
      if (proto.maintenance_phase) html += `<p><strong>${noProtocol ? 'Protect your baseline' : 'Maintenance'}:</strong> ${this._esc(proto.maintenance_phase)}</p>`;
      if (proto.expected_timeline) html += `<p><strong>Timeline:</strong> ${this._esc(proto.expected_timeline)}</p>`;
      if (proto.personalized_note) html += `<p>${this._esc(proto.personalized_note)}</p>`;
      html += `</div>`;
      return html;
    }

    _renderNudge(result) {
      if (!result.purchase_nudge) return null;
      const copy = String(result.purchase_nudge);
      const containsUnverifiedCommerceLanguage = /(discount|coupon|welcome email|subscribe|subscription|checkout|free shipping|complimentary shipping|guarantee|price)/i.test(copy);
      const safeCopy = containsUnverifiedCommerceLanguage
        ? 'If you want to address the structural pattern described here, begin with one complete reconstruction cycle and assess the result before changing the protocol.'
        : copy;
      return `<div class="qr__nudge">${this._esc(safeCopy)}</div>`;
    }

    _renderProducts(result, settings) {
      const products = (settings.products || []).filter(p => p !== null);
      if (products.length === 0) return null;

      // product_recommendation is optional (AI no longer outputs it)
      let howEachWorks = result.product_recommendation?.how_each_works || {};
      if (typeof howEachWorks === 'string') {
        try { howEachWorks = JSON.parse(howEachWorks); } catch (_) { howEachWorks = {}; }
      }

      let html = '';
      if (settings.heading) html += `<p class="qr__label">${this._esc(settings.heading)}</p>`;
      html += `<div class="qr__products"><div class="qr__product-grid">`;

      products.forEach((product, i) => {
        const reason = howEachWorks?.[product.handle] || product.subtitle || '';
        const badge = product.badge || `Step ${i + 1}`;

        html += `<div class="qr__product-card">`;
        html += `<span class="qr__product-badge">${this._esc(badge)}</span>`;
        html += `<div class="qr__product-image">`;
        if (product.image) {
          html += `<img src="${this._escAttr(product.image)}" alt="${this._escAttr(product.title)}" loading="lazy" width="400" height="400">`;
        } else {
          html += `<div class="qr__product-placeholder">${this._esc(product.title)}</div>`;
        }
        html += `</div>`;
        html += `<div class="qr__product-content">`;
        html += `<div class="qr__product-name"><a href="${this._escAttr(product.url)}">${this._esc(product.title)}</a></div>`;
        html += `<div class="qr__product-subtitle">${this._esc(reason)}</div>`;
        html += `<div class="qr__product-price">${this._esc(product.price)}</div>`;
        html += `<a href="${this._escAttr(product.url)}" class="qr__product-cta">Shop Now</a>`;
        html += `</div></div>`;
      });

      html += `</div></div>`;
      return html;
    }

    _renderCta(settings, result) {
      let html = `<div class="qr__cta">`;
      if (settings.primary_label && result.in_scope !== false) {
        const url = settings.primary_url || '/collections/all-products';
        html += `<a href="${this._escAttr(url)}" class="qr__cta-btn qr__cta-btn--primary">${this._esc(settings.primary_label)}</a>`;
      }
      if (settings.show_retake) {
        html += `<button type="button" class="qr__cta-btn qr__cta-btn--outline" data-retake>${this._esc(settings.retake_label || 'Retake Assessment')}</button>`;
      }
      html += `</div>`;
      return html;
    }

    /* ═══════════════════════════════════════════════════════════
       ADAPTIVE STREAMING — render the instant preliminary, then stream
       the full assessment beneath it. Layered fallback to non-stream
       /api/assessment, then a canned brand-safe result.
       ═══════════════════════════════════════════════════════════ */

    _afterResultsRendered(result, options = {}) {
      this.currentResult = result;
      this._syncSaveCopy(result);
      this._syncRecommendationCommerce(result);
      if (this.qaMode) {
        this._prepareQaPreview();
        return;
      }
      const acceptedIdentity = options.identitySaved === true
        || this.identitySaved === true
        || this._hasAcceptedIdentity();
      if (!acceptedIdentity && this.resultContextExpired) {
        this.currentEmail = '';
        this._expireResultActions();
        return;
      }
      if (acceptedIdentity) {
        this._commitStoredIdentityState();
        this._setSaveState('saved');
      } else {
        // A legacy email in result/session storage is not proof of a current
        // opt-in. Strip it and require a fresh explicit form submission.
        this.currentEmail = '';
        if (result && typeof result === 'object' && Object.prototype.hasOwnProperty.call(result, 'email')) {
          const normalized = window.AnatomyQuizSupport.normalizeResultForStorage(result);
          this.currentResult = normalized;
          try { sessionStorage.setItem('anatomy:quiz-results', JSON.stringify(normalized)); } catch (_error) {}
        }
        this._setSaveState('anonymous');
      }

      const storedOffer = this._storedOffer();
      const offer = options.offer || storedOffer;
      if (offer) this._applyOffer(offer);

      if (typeof Shopify !== 'undefined' && Shopify.designMode) return;
      if (!this._commerceAllowed(result)) return;
      if (!offer) this._ensureOffer().catch(error => this._setOfferUnavailable(error));
    }

    _syncRecommendationCommerce(result) {
      const root = this.querySelector('[data-recommended-start]');
      const sticky = this.querySelector('[data-recommendation-sticky]');
      const commerceAllowed = this._commerceAllowed(result);
      let entryPoint = result?.reconstruction_protocol?.entry_point || '';
      if (entryPoint !== 'full_system' && entryPoint !== 'targeted_start') {
        entryPoint = commerceAllowed ? 'full_system' : '';
      }

      this.querySelectorAll('[data-recommendation-panel]').forEach(panel => {
        panel.hidden = !commerceAllowed || panel.dataset.recommendationPanel !== entryPoint;
      });
      this.querySelectorAll('[data-recommendation-sticky-action]').forEach(action => {
        action.hidden = !commerceAllowed || action.dataset.recommendationStickyAction !== entryPoint;
      });

      if (root) root.hidden = !commerceAllowed || !entryPoint;
      if (sticky) {
        sticky.hidden = !commerceAllowed || !entryPoint || this.qaMode;
        sticky.dataset.recommendation = entryPoint;
        const title = sticky.querySelector('[data-recommendation-sticky-title]');
        if (title) {
          title.textContent = entryPoint === 'targeted_start'
            ? 'Molecular Reconstruction Leave-In'
            : 'Complete Reconstruction System';
        }
      }

      if (commerceAllowed && entryPoint && !this._recommendationCaptured) {
        this._recommendationCaptured = true;
        this._capture('quiz_recommended_start_viewed', {
          recommendation_entry_point: entryPoint,
          recommended_product_handle: entryPoint === 'targeted_start'
            ? 'leave-in-conditioner'
            : 'anatomy-complete-reconstruction-system'
        });
      }
    }

    _syncSaveCopy(result) {
      if (this._commerceAllowed(result)) return;
      const body = this.querySelector('[data-consultation-save-body]');
      const complete = this.querySelector('[data-consultation-save-complete-copy]');
      if (body) body.textContent = 'Get your private result link and guidance for protecting your current baseline.';
      if (complete) complete.textContent = 'Your private profile and baseline guidance are saved to your email.';
    }

    _prepareQaPreview() {
      this.currentOffer = null;
      this._setSaveState('anonymous');
      this.querySelectorAll('[data-consultation-offer]').forEach(card => {
        card.dataset.offerState = 'qa';
        delete card.dataset.variantId;
        const button = card.querySelector('[data-consultation-add]');
        const error = card.querySelector('[data-offer-error]');
        if (button) {
          button.disabled = true;
          button.removeAttribute('aria-busy');
          const label = button.querySelector('[data-offer-button-label]');
          if (label) label.textContent = 'Offer disabled in QA';
        }
        if (error) error.hidden = true;
      });
      const sticky = this.querySelector('[data-recommendation-sticky]');
      if (sticky) sticky.hidden = true;
    }

    _commerceAllowed(result = this.currentResult) {
      return !!result && result.in_scope !== false && result.commerce_allowed !== false && !result.assessment_unavailable;
    }

    _setSaveState(state, message = '') {
      const block = this.querySelector('[data-block-type="save_results"]');
      if (!block) return;
      const form = block.querySelector('[data-consultation-save-form]');
      const pending = block.querySelector('[data-consultation-save-pending]');
      const complete = block.querySelector('[data-consultation-save-complete]');
      const error = block.querySelector('[data-consultation-save-error]');
      const errorMessage = block.querySelector('[data-consultation-save-error-message]');
      if (form) form.hidden = state !== 'anonymous';
      if (pending) pending.hidden = state !== 'pending';
      if (complete) complete.hidden = state !== 'saved';
      if (error) error.hidden = state !== 'error' && state !== 'expired';
      if (errorMessage && message) errorMessage.textContent = message;
      const retry = error?.querySelector('[data-consultation-save-retry], [data-expired-retake]');
      if (retry && state === 'expired') {
        retry.removeAttribute('data-consultation-save-retry');
        retry.setAttribute('data-retake', '');
        retry.setAttribute('data-expired-retake', '');
        retry.textContent = 'Retake the consultation';
      } else if (retry && retry.hasAttribute('data-expired-retake')) {
        retry.removeAttribute('data-retake');
        retry.removeAttribute('data-expired-retake');
        retry.setAttribute('data-consultation-save-retry', '');
        retry.textContent = 'Try again';
      }
    }

    _resultContextIsUsable() {
      if (!this.resultContext || this.resultContextExpired) return false;
      const now = Date.now();
      const createdAt = Number(this.resultContext.ts);
      if (!Number.isFinite(createdAt) || createdAt <= 0 || now >= createdAt + 60 * 60 * 1000) return false;
      if (Number(this.resultContext.version) >= 2) {
        const expiresAt = Number(this.resultContext.sessionExpiresAt);
        return Number.isFinite(expiresAt) && expiresAt > now;
      }
      const tokenPrefix = String(this.resultContext.sessionToken || '').split('.', 1)[0];
      if (/^\d{10,16}$/.test(tokenPrefix) && Number(tokenPrefix) <= now) return false;
      return true;
    }

    _isResultContextExpiryError(error) {
      return error?.code === 'result_context_expired'
        || error?.name === 'QuizResultContextExpiredError';
    }

    // The one-hour session expiry guards results rendered from a live consultation.
    // A saved-link view never had that session, so it never expires on its account.
    _offerBlockedByExpiry(error) {
      if (this.savedLinkView) return false;
      return this.resultContextExpired || this._isResultContextExpiryError(error) || !this._resultContextIsUsable();
    }

    _expireResultActions() {
      this.resultContextExpired = true;
      this.resultContextStatus = 'expired';
      this.resultContext = null;
      try {
        sessionStorage.removeItem('anatomy:quiz-result-context');
        sessionStorage.removeItem('anatomy:quiz-offer');
      } catch (_error) {}
      this._setOfferExpired();
      this._setSaveState(
        'expired',
        'This consultation session has expired. Retake the consultation to save a new private profile.'
      );
    }

    async _finalizeKnownIdentity(email) {
      if (this.qaMode) return null;
      if (!this._resultContextIsUsable()) {
        this._expireResultActions();
        return null;
      }
      if (!this.resultContext || !email || this._identityCompletionPromise) {
        return this._identityCompletionPromise;
      }
      if (this.identitySaved || this._hasAcceptedIdentity()) {
        this._commitStoredIdentityState();
        this._setSaveState('saved');
        return null;
      }

      this.pendingEmail = email;
      this._setSaveState('pending');
      this._identityCompletionPromise = window.AnatomyQuizSupport.completeEmail(this, email, {
        leadEventId: this.pendingLeadEventId || '',
        captureContext: this.pendingLeadEventId ? 'results_save' : 'results_finalize',
        waitForCompletion: true,
      }).then(data => {
        if (!this._resultContextIsUsable()) {
          this._expireResultActions();
          return null;
        }
        this._commitAcceptedIdentity(email, data, {
          leadEventId: this.pendingLeadEventId || '',
          saveFlow: !!this.pendingLeadEventId,
        });
        return data;
      }).catch(error => {
        if (this._isResultContextExpiryError(error) || !this._resultContextIsUsable()) {
          this._expireResultActions();
          return null;
        }
        console.error('[Quiz Results] identity completion failed:', error);
        this._setSaveState('error', error.message || 'Your profile could not be saved yet. Please try again.');
        return null;
      }).finally(() => {
        this._identityCompletionPromise = null;
      });
      return this._identityCompletionPromise;
    }

    _commitAcceptedIdentity(email, data = {}, options = {}) {
      if (options.leadEventId) {
        window.AnatomyQuizSupport.track('Lead', {
          _fbEventId: options.leadEventId,
          content_name: 'Hair Consultation Results Save',
          value: 0,
          currency: 'USD',
        }, this);
      }
      if (data.offer) this._applyOffer(data.offer);
      this.identitySaved = true;
      this._commitStoredIdentityState();
      if (options.saveFlow) {
        this._capture('quiz_results_save_submitted', {});
        this._capture('quiz_results_save_success', {});
      }
      this._setSaveState('saved');
    }

    _commitStoredIdentityState() {
      this.identitySaved = true;
      const completion = window.AnatomyQuizSupport.replaceCompletionState
        ? window.AnatomyQuizSupport.replaceCompletionState(this, this.currentResult || {}, true)
        : null;
      if (completion) {
        this.currentResult = completion.result;
        this.resultContext = completion.context || this.resultContext;
      }
      this.currentEmail = '';
      this.pendingEmail = '';
      this.pendingLeadEventId = '';
      const input = this.querySelector('[data-consultation-save-form] input[type="email"]');
      if (input) input.value = '';
    }

    _storedOffer() {
      try {
        const stored = JSON.parse(sessionStorage.getItem('anatomy:quiz-offer') || 'null');
        if (stored && stored.sessionId === this.sessionId && this._validOffer(stored.offer)) return stored.offer;
      } catch (_error) {}
      return null;
    }

    _validOffer(offer) {
      return !!offer
        && /^AN10[A-HJ-NP-Z2-9]{4}$/.test(String(offer.code || ''))
        && Number.isFinite(Number(offer.original_price_cents))
        && Number.isFinite(Number(offer.discounted_price_cents));
    }

    _offerIsActive(offer) {
      const expiresAt = Date.parse(offer?.expires_at || '');
      return Number.isFinite(expiresAt) && expiresAt > Date.now();
    }

    async _ensureOffer() {
      if (this.qaMode) throw new Error('Private offers are disabled in QA mode.');
      if (this.savedLinkView) {
        // The saved record carries its own offer; there is no session to mint another.
        if (this._validOffer(this.currentOffer)) return this.currentOffer;
        throw new Error('No private offer is attached to this saved result.');
      }
      if (!this._resultContextIsUsable() || !this.sessionId) {
        this._expireResultActions();
        throw new Error('This consultation session has expired. Retake the consultation to generate a new offer.');
      }
      if (this._validOffer(this.currentOffer)) return this.currentOffer;
      if (this._offerPromise) return this._offerPromise;

      this._setOfferLoading();
      this._offerPromise = window.AnatomyQuizSupport.workerFetch(this, '/api/consultation-offer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: this.sessionId,
          anatomy_visitor_id: this.visitorId(),
        }),
      }).then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `Offer API ${response.status}`);
        if (!this._validOffer(data.offer)) throw new Error('The consultation offer response was incomplete.');
        this._applyOffer(data.offer);
        return data.offer;
      }).finally(() => {
        this._offerPromise = null;
      });
      return this._offerPromise;
    }

    _applyOffer(offer) {
      if (this.qaMode) return;
      if (!this._validOffer(offer)) return;
      this.currentOffer = offer;
      const isActive = this._offerIsActive(offer);
      try {
        sessionStorage.setItem('anatomy:quiz-offer', JSON.stringify({ sessionId: this.sessionId, offer }));
      } catch (_error) {}

      this.querySelectorAll('[data-consultation-offer]').forEach(card => {
        if (card.dataset.productHandle && offer.product_handle && card.dataset.productHandle !== offer.product_handle) return;
        const variantId = String(offer.variant_id || '').match(/^gid:\/\/shopify\/ProductVariant\/(\d+)$/)?.[1] || '';
        const original = card.querySelector('[data-offer-original]');
        const discounted = card.querySelector('[data-offer-discounted]');
        const code = card.querySelector('[data-offer-code]');
        const expiry = card.querySelector('[data-offer-expiry]');
        const button = card.querySelector('[data-consultation-add]');
        const error = card.querySelector('[data-offer-error]');
        if (original) original.textContent = this._formatMoney(offer.original_price_cents, offer.currency);
        if (discounted) discounted.textContent = this._formatMoney(offer.discounted_price_cents, offer.currency);
        if (code) code.textContent = offer.code;
        if (expiry) expiry.textContent = isActive ? this._formatOfferExpiry(offer.expires_at) : 'Private offer expired';
        if (error) error.hidden = true;
        if (button) {
          if (variantId) card.dataset.variantId = variantId;
          button.disabled = !variantId;
          button.removeAttribute('aria-busy');
          const label = button.querySelector('[data-offer-button-label]');
          if (label) label.textContent = variantId ? 'Add system to cart' : 'System unavailable';
        }
        card.dataset.offerState = isActive ? 'ready' : 'expired';
      });

      this._capture('quiz_consultation_offer_issued', {
        product_handle: offer.product_handle || '',
        discount_percent: Number(offer.percent) || 10,
      });
    }

    _setOfferLoading() {
      this.querySelectorAll('[data-consultation-offer]').forEach(card => {
        card.dataset.offerState = 'loading';
        const button = card.querySelector('[data-consultation-add]');
        if (!button) return;
        button.setAttribute('aria-busy', 'true');
        const label = button.querySelector('[data-offer-button-label]');
        if (label) label.textContent = 'Preparing your private offer';
      });
    }

    _setOfferError(error) {
      if (this._offerBlockedByExpiry(error)) {
        this._expireResultActions();
        return;
      }
      console.error('[Quiz Results] offer failed:', error);
      this.querySelectorAll('[data-consultation-offer]').forEach(card => {
        card.dataset.offerState = 'error';
        const button = card.querySelector('[data-consultation-add]');
        const message = card.querySelector('[data-offer-error]');
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          const label = button.querySelector('[data-offer-button-label]');
          if (label) label.textContent = 'Retry private offer';
        }
        if (message) {
          message.hidden = false;
          message.textContent = error.message || 'Your private offer could not be prepared yet.';
        }
      });
    }

    // The private offer could not be prepared (for example the AI assessment failed or the
    // offer service is down). The purchase must never depend on it: the card falls back to
    // the standard price and the add button keeps working with the variant rendered by Liquid.
    _setOfferUnavailable(error) {
      if (this._offerBlockedByExpiry(error)) {
        this._expireResultActions();
        return;
      }
      console.warn('[Quiz Results] private offer unavailable:', error);
      this.currentOffer = null;
      this.querySelectorAll('[data-consultation-offer]').forEach(card => {
        card.dataset.offerState = 'standard';
        const variantId = card.dataset.variantId || '';
        const button = card.querySelector('[data-consultation-add]');
        const message = card.querySelector('[data-offer-error]');
        if (button) {
          button.disabled = !variantId;
          button.removeAttribute('aria-busy');
          const label = button.querySelector('[data-offer-button-label]');
          if (label) label.textContent = variantId ? 'Add system to cart' : 'System unavailable';
        }
        if (message) {
          message.hidden = false;
          message.textContent = 'Your private offer is not available right now. You can still start at the standard price.';
        }
      });
    }

    _setOfferExpired() {
      this.currentOffer = null;
      this.querySelectorAll('[data-consultation-offer]').forEach(card => {
        card.dataset.offerState = 'expired';
        delete card.dataset.variantId;
        const button = card.querySelector('[data-consultation-add]');
        const message = card.querySelector('[data-offer-error]');
        if (button) {
          button.disabled = true;
          button.removeAttribute('aria-busy');
          delete button.dataset.loading;
          const label = button.querySelector('[data-offer-button-label]');
          if (label) label.textContent = 'Retake consultation for a new offer';
        }
        if (message) {
          message.hidden = false;
          message.textContent = 'This consultation session has expired.';
        }
      });
    }

    _formatMoney(cents, currency) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency || 'USD',
          minimumFractionDigits: 2,
        }).format(Number(cents) / 100);
      } catch (_error) {
        return `$${(Number(cents) / 100).toFixed(2)}`;
      }
    }

    _formatOfferExpiry(value) {
      const date = new Date(value);
      if (!Number.isFinite(date.getTime())) return '';
      return `Valid until ${new Intl.DateTimeFormat(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
      }).format(date)}`;
    }

    async _addConsultationSystem(button) {
      if (this.qaMode) return;
      if (button.dataset.loading === 'true') return;
      button.dataset.loading = 'true';
      button.disabled = true;
      const label = button.querySelector('[data-offer-button-label]');
      const originalLabel = label ? label.textContent : '';
      if (label) label.textContent = 'Adding to cart';
      this._capture('quiz_consultation_bundle_add_attempt', {});

      try {
        let offer = null;
        try {
          offer = await this._ensureOffer();
        } catch (offerError) {
          // An expired consultation keeps its retake flow; any other offer failure must not block the purchase.
          if (this._offerBlockedByExpiry(offerError)) {
            throw offerError;
          }
          this._capture('quiz_consultation_offer_unavailable', {
            error_type: offerError.message || 'unknown',
            context: 'bundle_add',
          });
          this._setOfferUnavailable(offerError);
          button.disabled = true;
          if (label) label.textContent = 'Adding to cart';
        }
        const card = button.closest('[data-consultation-offer]');
        const variantId = Number(card?.dataset.variantId || 0);
        if (!Number.isSafeInteger(variantId) || variantId <= 0) throw new Error('The Complete System variant is unavailable.');
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || 'The Complete System could not be added to cart.');
        const nextUrl = offer && this._offerIsActive(offer)
          ? new URL(offer.discount_url, window.location.origin)
          : new URL('/cart', window.location.origin);
        if (nextUrl.origin !== window.location.origin) throw new Error('The private offer link was invalid.');
        this._capture('quiz_consultation_bundle_add_success', {
          product_handle: offer?.product_handle || card?.dataset.productHandle || '',
          discount_percent: offer ? (Number(offer.percent) || 10) : 0,
          offer_applied: !!offer,
        });
        window.location.assign(nextUrl.href);
      } catch (error) {
        this._capture('quiz_consultation_bundle_add_failed', { error_type: error.message || 'unknown' });
        this._setOfferError(error);
        if (this.savedLinkView || !this.resultContextExpired) {
          button.disabled = false;
          if (label) label.textContent = this.currentOffer ? 'Add system to cart' : (originalLabel || 'Retry private offer');
        }
      } finally {
        delete button.dataset.loading;
      }
    }

    async _saveConsultation(form) {
      if (this.qaMode) return;
      if (form.dataset.loading === 'true') return;
      if (!form.reportValidity()) return;
      const input = form.querySelector('input[type="email"]');
      const button = form.querySelector('button[type="submit"]');
      const status = form.querySelector('[data-save-status]');
      const email = String(input?.value || '').trim().toLowerCase();
      if (!email || !this._resultContextIsUsable()) {
        if (status) status.textContent = 'This consultation session has expired. Retake the consultation to save a new profile.';
        this._expireResultActions();
        return;
      }

      form.dataset.loading = 'true';
      if (button) button.disabled = true;
      if (status) status.textContent = 'Saving your private profile...';
      this.pendingEmail = email;
      const leadEventId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      this.pendingLeadEventId = leadEventId;
      try {
        this._setSaveState('pending');
        const data = await window.AnatomyQuizSupport.completeEmail(this, email, {
          leadEventId,
          captureContext: 'results_save',
          waitForCompletion: true,
        });
        if (!this._resultContextIsUsable()) {
          this._expireResultActions();
          return;
        }
        this._commitAcceptedIdentity(email, data, { leadEventId, saveFlow: true });
      } catch (error) {
        if (this._isResultContextExpiryError(error) || !this._resultContextIsUsable()) {
          this._expireResultActions();
          return;
        }
        if (status) status.textContent = error.message || 'Your profile could not be saved yet. Please try again.';
        this._setSaveState('error', error.message || 'Your profile could not be saved yet. Please try again.');
        if (button) button.disabled = false;
      } finally {
        delete form.dataset.loading;
      }
    }

    _capture(event, properties) {
      if (this.qaMode) return;
      const support = window.AnatomyQuizSupport;
      if (!support || !support.analyticsAllowed || !support.analyticsAllowed()) return;
      if (!window.posthog || typeof window.posthog.capture !== 'function') return;
      window.posthog.capture(event, Object.assign({
        anatomy_quiz_session_id: this.sessionId || '',
      }, properties || {}));
    }

    _startAdaptiveResults(prelim) {
      this.config.workerUrl = String(prelim.workerUrl || this.config.workerUrl || '').replace(/\/+$/, '');
      this.sessionId = prelim.sessionId || '';
      this.sessionToken = prelim.sessionToken || '';
      this.sessionExpiresAt = Number(prelim.sessionExpiresAt) || 0;
      this.anatomyVisitorId = window.AnatomyQuizSupport.analyticsAllowed()
        ? (prelim.anatomyVisitorId || '')
        : '';
      this.conversationHistory = Array.isArray(prelim.conversationHistory) ? prelim.conversationHistory : [];
      const prelimCreatedAt = Number(prelim.ts);
      const prelimExpiresAt = Number.isFinite(prelimCreatedAt) && prelimCreatedAt > 0 && this.sessionExpiresAt > 0
        ? Math.min(prelimCreatedAt + 60 * 60 * 1000, this.sessionExpiresAt)
        : 0;
      if (!prelimExpiresAt || Date.now() >= prelimExpiresAt) {
        try { sessionStorage.removeItem('anatomy:quiz-prelim'); } catch (_error) {}
        this.conversationHistory = [];
        this._prelim = null;
        this.resultContextExpired = true;
        this.resultContextStatus = 'expired';
        this._finalized = true;
        this.dataset.resultsFinalized = 'true';
        this.dataset.resultsSource = 'expired';
        this._showEmpty(
          'Consultation session expired',
          'Retake the consultation to create a new Molecular Damage Profile.'
        );
        return;
      }
      if (!this.resultContext) {
        const firstTurn = this.conversationHistory[0] || {};
        const primaryConcern = firstTurn.input_type === 'freeform'
          ? 'freeform'
          : String(firstTurn.answer || 'unspecified').replace(/\s+/g, ' ').trim().slice(0, 160) || 'unspecified';
        this.resultContext = {
          version: 2,
          sessionId: this.sessionId,
          sessionToken: this.sessionToken,
          sessionExpiresAt: this.sessionExpiresAt,
          anatomyVisitorId: this.anatomyVisitorId,
          identitySaved: false,
          summary: {
            version: 2,
            sessionId: this.sessionId,
            completedAt: new Date().toISOString(),
            identitySaved: false,
            primaryConcern,
            totalQuestions: Math.max(1, Math.min(6, this.conversationHistory.length || 1)),
          },
          quizMode: (prelim && prelim.quizMode) || 'adaptive_bank',
          totalQuestions: Number(prelim && prelim.totalQuestions)
            || Math.max(1, Math.min(6, this.conversationHistory.length || 1)),
          ts: Date.now(),
        };
      }
      this.resultContextStatus = 'valid';
      this.resultContextExpired = false;
      this.identitySaved = this._hasAcceptedIdentity(this.resultContext);
      this.currentEmail = '';
      this._sessionPromise = null;
      if (this.loadingEl) this.loadingEl.hidden = true;
      this._injectStreamUI();
      this._renderPreliminary((prelim && prelim.preliminary) || {});

      const canStream = !!(prelim && prelim.streamAssessment && prelim.workerUrl)
        && typeof window.ReadableStream !== 'undefined'
        && typeof fetch === 'function';

      if (canStream) {
        this._streamAssessment(prelim);
      } else {
        this._fallbackNonStream(prelim);
      }
    }

    _injectStreamUI() {
      if (!document.getElementById('anatomy-qr-stream-style')) {
        const style = document.createElement('style');
        style.id = 'anatomy-qr-stream-style';
        style.textContent = QR_STREAM_CSS;
        document.head.appendChild(style);
      }
      if (this.streamEl) return;

      const wrap = document.createElement('div');
      wrap.className = 'qr-stream';
      wrap.setAttribute('data-results-stream', '');
      wrap.innerHTML =
        '<div class="qr-prelim" data-results-prelim></div>' +
        '<div class="qr-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-live="polite">' +
          '<div class="qr-progress__track"><div class="qr-progress__bar" data-progress-bar style="width:0%"></div></div>' +
          '<p class="qr-progress__label" data-progress-label></p>' +
        '</div>';

      if (this.containerEl && this.containerEl.parentNode) {
        this.containerEl.parentNode.insertBefore(wrap, this.containerEl);
      } else {
        this.appendChild(wrap);
      }

      this.streamEl = wrap;
      this.prelimEl = wrap.querySelector('[data-results-prelim]');
      this.progressBarEl = wrap.querySelector('[data-progress-bar]');
      this.progressLabelEl = wrap.querySelector('[data-progress-label]');
      this.progressEl = wrap.querySelector('.qr-progress');
      if (this.containerEl) this.containerEl.hidden = false;
    }

    _renderPreliminary(prelim) {
      if (!this.prelimEl) return;
      prelim = prelim || {};
      const sev = prelim.damage_severity || '';
      const insight = prelim.key_insight || prelim.what_is_happening || '';
      const parts = ['<div class="qr-prelim__card">'];
      if (sev) parts.push('<span class="qr-prelim__severity qr__severity--' + this._escAttr(sev) + '">' + this._esc(this._formatSeverity(sev)) + '</span>');
      if (prelim.damage_profile_headline) parts.push('<h2 class="qr-prelim__headline">' + this._esc(prelim.damage_profile_headline) + '</h2>');
      if (prelim.damage_profile_summary) parts.push('<p class="qr-prelim__summary">' + this._esc(prelim.damage_profile_summary) + '</p>');
      if (insight) parts.push('<p class="qr-prelim__insight">' + this._esc(insight) + '</p>');
      parts.push('</div>');
      this.prelimEl.innerHTML = parts.join('');
    }

    async _streamAssessment(prelim) {
      this._setProgress(0.04, 'Reading your profile');
      this._armWatchdogs(prelim);
      this._streamAbort = new AbortController();

      let res;
      try {
        res = await window.AnatomyQuizSupport.workerFetch(this, '/api/assessment-stream', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
          body: JSON.stringify({
            session_id: prelim.sessionId,
            anatomy_visitor_id: this.visitorId(),
            customer_privacy: window.AnatomyQuizSupport.customerPrivacySnapshot(),
            conversation_history: prelim.conversationHistory || [],
            profile: prelim.runningProfile || null,
            products: prelim.products || []
          }),
          signal: this._streamAbort.signal
        });
      } catch (err) {
        if (this._isResultContextExpiryError(err) || !this._resultContextIsUsable()) {
          this._expireResultActions();
          return this._finalizeStream(this._getFallbackResult());
        }
        console.error('[Quiz Results] stream open failed:', err);
        return this._fallbackNonStream(prelim);
      }

      const ctype = res.headers.get('Content-Type') || '';
      if (res.status === 202) return this._fallbackNonStream(prelim);
      if (!res.ok && ctype.includes('application/json')) {
        const failure = await res.json().catch(() => ({}));
        if (failure.retryable === false) {
          this._assessmentFailureCode = failure.code || 'assessment_unavailable';
          return this._finalizeStream(this._getFallbackResult());
        }
      }
      if (!res.ok || !res.body || ctype.indexOf('text/event-stream') === -1) {
        // Worker may signal an explicit fallback (501/502 JSON) or an in-app
        // webview may not expose res.body — non-stream fetch instead.
        return this._fallbackNonStream(prelim);
      }

      try {
        await window.AnatomyQuizSupport.consumeEventStream(res, (event, data) => {
          if (!this._finalized) this._onStreamEvent(event, data);
        });
      } catch (err) {
        if (!this._finalized) {
          console.error('[Quiz Results] stream read error:', err);
          return this._fallbackNonStream(prelim);
        }
      }

      if (!this._finalized) this._fallbackNonStream(prelim);  // ended without `complete`
    }

    _onStreamEvent(event, dataStr) {
      let d = {};
      if (dataStr) { try { d = JSON.parse(dataStr); } catch (e) { return; } }
      switch (event) {
        case 'meta':
          if (typeof d.totalFields === 'number') this._totalFields = d.totalFields;
          break;
        case 'status':
          // First real token: clear the no-first-token guard on `writing` only
          // (NOT the `meta`/`thinking` frame).
          if (d.phase === 'writing') this._clearFirstTokenGuard();
          break;
        case 'progress':
          this._clearFirstTokenGuard();
          this._setProgress(d.value, d.label);
          break;
        case 'field':
          if (d.key === 'damage_severity') { this._clearFirstTokenGuard(); this._streamSeverity(d.value); }
          break;
        case 'section':
          this._clearFirstTokenGuard();
          this._streamAppend(d.key, d.append || '');
          break;
        case 'complete':
          if (this._isCompleteProfile(d.profile)) this._finalizeStream(d.profile);
          else if (!this._finalized) this._fallbackNonStream(this._prelim);
          break;
        case 'error':
          this._assessmentFailureCode = d.code || 'assessment_unavailable';
          if (d.retryable === false) this._finalizeStream(this._getFallbackResult());
          else if (!this._finalized) this._fallbackNonStream(this._prelim);
          break;
        default:
          break;
      }
    }

    _clearFirstTokenGuard() {
      if (this._gotFirstToken) return;
      this._gotFirstToken = true;
      if (this._firstTokenTimer) { clearTimeout(this._firstTokenTimer); this._firstTokenTimer = null; }
    }

    _armWatchdogs(prelim) {
      let firstTokenTimeout = 40000;
      let hardCapTimeout = 60000;
      try {
        const qaTimers = new URLSearchParams(window.location.search).get('anatomy_qa') === '1'
          ? window.__ANATOMY_QUIZ_QA_TIMERS__
          : null;
        if (qaTimers) {
          firstTokenTimeout = Math.max(10, Number(qaTimers.firstTokenMs) || firstTokenTimeout);
          hardCapTimeout = Math.max(firstTokenTimeout + 10, Number(qaTimers.hardCapMs) || hardCapTimeout);
        }
      } catch (e) { /* production defaults remain authoritative */ }
      this._firstTokenTimer = setTimeout(() => {
        if (!this._gotFirstToken && !this._finalized) {
          console.warn('[Quiz Results] no first token yet; assessment remains in flight');
          this._setProgress(0.12, 'Still reading your profile');
        }
      }, firstTokenTimeout);
      this._hardCapTimer = setTimeout(() => {
        if (!this._finalized) {
          console.warn('[Quiz Results] stream hard cap; polling the same assessment');
          if (this._streamAbort) this._streamAbort.abort();
          this._fallbackNonStream(prelim);
        }
      }, hardCapTimeout);
    }

    _isCompleteProfile(profile) {
      const hasProfileCore = !!(
        profile
        && typeof profile === 'object'
        && profile.damage_profile_headline
        && profile.damage_profile_summary
        && profile.what_is_happening
      );
      if (!hasProfileCore) return false;
      if (profile.in_scope === false) return true;
      return !!(profile.how_anatomy_works_for_you && profile.reconstruction_protocol);
    }

    _streamSeverity(value) {
      if (!value) return;
      // Suppress the override if it equals the preliminary severity (B10).
      const prelimSev = this._prelim && this._prelim.preliminary && this._prelim.preliminary.damage_severity;
      if (prelimSev && prelimSev === value) return;
      // Single source of truth: when the assessment overrides the band, the
      // prelim card's chip must not keep contradicting it on screen.
      const prelimChip = this.prelimEl && this.prelimEl.querySelector('.qr-prelim__severity');
      if (prelimChip) {
        prelimChip.className = 'qr-prelim__severity qr__severity--' + this._escAttr(value);
        prelimChip.textContent = this._formatSeverity(value);
      }
      const block = this.containerEl && this.containerEl.querySelector('[data-block-type="severity_badge"]');
      if (!block) return;
      block.hidden = false;
      block.innerHTML = '<span class="qr__severity qr__severity--' + this._escAttr(value) + '">' + this._esc(this._formatSeverity(value)) + '</span>';
    }

    _streamAppend(key, chunk) {
      const map = STREAM_FIELD_MAP[key];
      if (!map) return;
      // The streamed assessment replaces the preliminary card — hide it at the
      // first prose token so two headlines/severities never stack on screen.
      if (this.prelimEl && !this.prelimEl.hidden) this.prelimEl.hidden = true;
      const block = this.containerEl && this.containerEl.querySelector('[data-block-type="' + map.type + '"]');
      if (!block) return;
      block.hidden = false;
      let el = this._streamEls[key];
      if (!el) {
        block.innerHTML = '';
        el = document.createElement(map.tag);
        el.className = map.cls;
        el.style.whiteSpace = 'pre-wrap';
        block.appendChild(el);
        this._streamEls[key] = el;
      }
      el._rawQuizCopy = (el._rawQuizCopy || '') + chunk;
      const safeStreamCopy = this._safeCopy(el._rawQuizCopy);
      el.textContent = key === 'damage_profile_headline' ? this._sentenceCase(safeStreamCopy) : safeStreamCopy;
    }

    _setProgress(value, label) {
      const pct = Math.max(0, Math.min(100, Math.round((value || 0) * 100)));
      if (this.progressBarEl) this.progressBarEl.style.width = pct + '%';
      if (this.progressEl && pct !== this._lastAnnouncedPct) {
        this.progressEl.setAttribute('aria-valuenow', String(pct));
        this._lastAnnouncedPct = pct;
      }
      if (this.progressLabelEl && label) this.progressLabelEl.textContent = label;
    }

    _finalizeStream(profile) {
      if (this._finalized) return;
      this._finalized = true;
      this._streamAbort?.abort();
      this._recoveryAbort?.abort();
      if (this._firstTokenTimer) { clearTimeout(this._firstTokenTimer); this._firstTokenTimer = null; }
      if (this._hardCapTimer) { clearTimeout(this._hardCapTimer); this._hardCapTimer = null; }
      if (!this._resultContextIsUsable()) this._expireResultActions();

      const result = (profile && typeof profile === 'object') ? profile : this._getFallbackResult();
      const completion = window.AnatomyQuizSupport.replaceCompletionState(this, result, this.identitySaved === true);
      const storedResult = completion?.result || result;
      this.resultContext = completion?.context || this.resultContext;
      this.currentEmail = '';

      this._setProgress(1, 'Complete');
      this._renderBlocks(storedResult);     // authoritative finalizer overwrites progressive prose + renders structured blocks
      this._afterResultsRendered(storedResult);
      this._renderDebug(storedResult);
      this._prelim = null;
      this.conversationHistory = [];
      if (this.streamEl) this.streamEl.hidden = true;   // hide prelim + progress chrome
      this.dataset.resultsFinalized = 'true';
      this.dataset.resultsSource = this._fallbackStarted ? 'fallback' : 'stream';
      if (storedResult.assessment_unavailable) {
        this._capture('quiz_assessment_failed', { error_code: this._assessmentFailureCode || 'assessment_unavailable' });
      } else {
        this._track('QuizResultsViewed', { damage_severity: storedResult.damage_severity || null });
      }
    }

    async _fallbackNonStream(prelim) {
      if (this._finalized || this._fallbackStarted) return;
      this._fallbackStarted = true;
      if (this._firstTokenTimer) { clearTimeout(this._firstTokenTimer); this._firstTokenTimer = null; }
      if (this._hardCapTimer) { clearTimeout(this._hardCapTimer); this._hardCapTimer = null; }
      this._setProgress(0.5, 'Composing your profile');

      if (!prelim || !prelim.workerUrl) return this._finalizeStream(this._getFallbackResult());
      let recoveryWindowMs = 90000;
      let recoveryDelayMs = 2000;
      try {
        const qaTimers = new URLSearchParams(window.location.search).get('anatomy_qa') === '1'
          ? window.__ANATOMY_QUIZ_QA_TIMERS__
          : null;
        if (qaTimers) {
          recoveryWindowMs = Math.max(50, Number(qaTimers.recoveryMs) || recoveryWindowMs);
          recoveryDelayMs = Math.max(10, Number(qaTimers.recoveryPollMs) || recoveryDelayMs);
        }
      } catch (e) { /* production defaults remain authoritative */ }
      const deadline = Date.now() + recoveryWindowMs;
      let recoveryErrorLogged = false;
      let failedAttempts = 0;
      while (!this._finalized && Date.now() < deadline) {
        this._recoveryAbort = new AbortController();
        const requestTimeout = setTimeout(() => this._recoveryAbort?.abort(), Math.max(1, deadline - Date.now()));
        try {
          const res = await window.AnatomyQuizSupport.workerFetch(this, '/api/assessment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_id: prelim.sessionId,
              anatomy_visitor_id: this.visitorId(),
              customer_privacy: window.AnatomyQuizSupport.customerPrivacySnapshot(),
              conversation_history: prelim.conversationHistory || [],
              profile: prelim.runningProfile || null,
              products: prelim.products || []
            }),
            signal: this._recoveryAbort.signal
          });
          if (res.status === 202) {
            const pending = await res.json().catch(() => ({}));
            const delay = Math.max(10, Math.min(5000, Number(pending.retry_after_ms) || recoveryDelayMs));
            this._setProgress(0.6, 'Finishing your profile');
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          if (!res.ok) {
            const failure = await res.json().catch(() => ({}));
            this._assessmentFailureCode = failure.code || 'assessment_unavailable';
            if (failure.retryable === false || [400, 401, 403, 404, 422].includes(res.status)) break;
            const error = new Error('assessment ' + res.status);
            error.retryAfterMs = Number(res.headers.get('Retry-After')) * 1000 || 0;
            throw error;
          }
          const data = await res.json();
          const profile = data.assessment || data.profile || data;
          if (!this._isCompleteProfile(profile)) throw new Error('assessment incomplete');
          return this._finalizeStream(profile);
        } catch (err) {
          if (this._finalized || ++failedAttempts >= 3) break;
          if (this._isResultContextExpiryError(err) || !this._resultContextIsUsable()) {
            this._expireResultActions();
            break;
          }
          if (!recoveryErrorLogged) {
            console.error('[Quiz Results] assessment recovery failed:', err);
            recoveryErrorLogged = true;
          }
          const retryDelay = Math.max(recoveryDelayMs, Number(err && err.retryAfterMs) || 0);
          const remaining = deadline - Date.now();
          if (retryDelay >= remaining) break;
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        } finally {
          clearTimeout(requestTimeout);
        }
      }
      this._finalizeStream(this._getFallbackResult());
    }

    _getFallbackResult() {
      return window.AnatomyQuizSupport.getFallbackResult();
    }

    _track(eventName, data = {}) {
      if (this.qaMode) return;
      this._emitAssessmentViewed(eventName, data);
    }

    _emitAssessmentViewed(_eventName, data = {}) {
      // Once-per-session guard prevents cache/stream render paths from double firing.
      try {
        const k = 'anatomy:quiz-aview';
        if (sessionStorage.getItem(k)) return;
      } catch (e) {}
      const props = {
        damage_severity: data.damage_severity || null,
        total_questions: Number(data.total_questions) || Number(this.resultContext && this.resultContext.totalQuestions) || this.conversationHistory.length || 0,
        assessment_source: 'results_page', render_path: data.render_path || 'stream'
      };
      const outcome = window.AnatomyQuizSupport.track(
        'QuizAssessmentViewed',
        props,
        this,
        { dispatchAssessmentView: true }
      );
      if (!outcome || (!outcome.emitted && !outcome.privacy_loading)) return;
      try { sessionStorage.setItem('anatomy:quiz-aview', '1'); } catch (e) {}
    }

    _renderDebug(result) {
      if (!this.debugEl || !this.config.debug) return;
      this.debugEl.hidden = false;
      const severity = result.damage_severity || '?';
      const keys = Object.keys(result).length;
      this.debugEl.innerHTML =
        `<details class="qr-debug__details" open>` +
        `<summary class="qr-debug__summary">Debug: Assessment — severity: ${this._esc(severity)} — ${keys} keys</summary>` +
        `<pre class="qr-debug__json">${this._esc(JSON.stringify(result, null, 2))}</pre>` +
        `</details>`;
    }

    _esc(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = this._safeCopy(str);
      return div.innerHTML;
    }

    _safeCopy(value) {
      return String(value).replace(/\b(?:Okendo|Klaviyo)\b/gi, 'the program');
    }

    _sentenceCase(value) {
      return String(value).replace(/[A-Za-z]/, letter => letter.toUpperCase());
    }

    _escAttr(str) {
      if (!str) return '';
      return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  });
}
